import { Component, Input, OnInit } from '@angular/core';
import { Platform } from '@ionic/angular';
import * as moment from 'moment';
import { Observable } from 'rxjs';

import { Intercom } from '@awesome-cordova-plugins/intercom/ngx';
import { AVATARS } from '@constants/miscellaneous';
import { IGeneralConstants, ISubscriptionPromo } from '@interfaces/general-constants';
import { IUserSocialProfile, IUser } from '@interfaces/users';
import { AuthService } from '@services/auth/auth.service';
import { FirebaseService } from '@services/firebase/firebase.service';
import { NavigationService } from '@services/navigation/navigation.service';
import { UserService } from '@services/user/user.service';
import Utils from '@util/util';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.page.html',
  styleUrls: ['./profile.page.scss'],
})
export class ProfilePage implements OnInit {
  @Input() selected: string;
  userInfo: IUser;
  activeCashReInvest: boolean;
  backButtonText: string;
  proChecked: boolean;
  isMobileApp: boolean;
  isPro: boolean;
  profilePic: string;
  socialProfile: IUserSocialProfile;
  currentPromo: ISubscriptionPromo;
  userIsEligibleForPromo: boolean;
  showAnnualPromo: boolean;
  showMonthlyPromo: boolean;
  loading: boolean;
  showPromo: boolean;

  constructor (
    private platform: Platform,
    private authService: AuthService,
    private firebaseService: FirebaseService,
    private userService: UserService,
    private navigationService: NavigationService,
    private intercomService: Intercom,
  ) {
    this.backButtonText = this.platform.is('ios') ? 'Atrás' : '';
    this.isMobileApp = this.platform.is('cordova');
    this.showAnnualPromo = false;
    this.showMonthlyPromo = false;
    this.loading = true;
  }

  ngOnInit() {
    Observable.combineLatest([
      this.authService.currentUserSource,
      this.firebaseService.currentGeneralConstants,
    ]).subscribe(async ([userInfo, generalConstants]: [IUser, IGeneralConstants]) => {
      if (userInfo && generalConstants) {
        this.userInfo = JSON.parse(JSON.stringify(userInfo));
        this.activeCashReInvest = generalConstants.featureToggles.cashReInvest;
        this.currentPromo = generalConstants.subscriptionPlans.currentPromo;
        if (this.userInfo && this.checkIfPromoIsActive()) {
          this.userIsEligibleForPromo = await this.checkIfUserIsEligibleForPromo();
          this.showAnnualPromo = this.userIsEligibleForPromo && this.currentPromo.periodicity.includes('yearly');
          this.showMonthlyPromo = this.userIsEligibleForPromo && this.currentPromo.periodicity.includes('monthly');
          this.showPromo = this.showAnnualPromo || this.showMonthlyPromo;
        }
        this.isPro = this.userInfo && this.userInfo.subscription === 'pro';
        this.subscribeToOwnProfileChanges();
        if (!this.socialProfile) {
          const animalImageFromUID = userInfo.uid.split('').map(s => s.charCodeAt(0)).reduce((a, b) => a + b, 0) % 12;
          this.profilePic = AVATARS[animalImageFromUID];
        }
      }
    });
    this.loading = false;
  }

  signOut() {
    this.firebaseService.logEvent('logout', {});
    this.navigationService.goToRoot('/persistent-login', 'back').then(() => {
      this.authService.signOut();
      this.authService.userLoggedOut = true;
    });
  }

  openIntercom() {
    this.firebaseService.logEvent('report_a_problem_from_profile', {});
    this.firebaseService.logEvent('report_a_problem_from_profile_new', {});
    this.navigationService.openInBrowser('https://ayuda.racional.cl');
  }

  goToMySub() {
    this.firebaseService.logEvent('go_to_my_subscription_from_profile', {});
    this.navigationService.goTo('subscription/my-subscription');
  }

  goToPro(event) {
    event.stopPropagation();
    this.firebaseService.logEvent('go_to_pro_details_from_profile', {});
    this.firebaseService.logEvent(
      this.showPromo ? 'go_to_pro_details_from_profile_promo' : 'go_to_pro_details_from_profile_no_promo',
      { user_id: this.userInfo.uid },
    );
    this.navigationService.goTo('subscription/pro-details');
  }

  goToCompleteInfo() {
    this.firebaseService.logEvent('complete_info_from_profile', {});
    this.firebaseService.logEvent('complete_info_from_profile_new', {});
    this.navigationService.goTo('my-data');
  }

  goToInvestments() {
    this.firebaseService.logEvent('investments_from_profile', {});
    this.navigationService.goTo('investments');
  }

  subscribeToOwnProfileChanges() {
    this.userService.currentSocialProfileSource.subscribe((profile) => {
      if (profile) {
        this.socialProfile = profile;
        this.profilePic = this.socialProfile.profileImage ? this.socialProfile.profileImage : AVATARS[profile ? profile.animalImage : 0];
      }
    });
  }

  goToMySubscription() {
    this.firebaseService.logEvent('my_subscription_from_my_account', {});
    this.navigationService.goTo('subscription/my-subscription');
  }

  goToReferrals() {
    this.firebaseService.logEvent('referrals_from_my_account', {});
    this.firebaseService.logEvent('referrals_from_my_account_new', {});
    this.navigationService.goTo('referrals');
  }

  goToSettings() {
    this.firebaseService.logEvent('settings_from_my_account', {});
    this.navigationService.goTo('settings');
  }

  goToSocialProfile() {
    this.firebaseService.logEvent('social_profile_from_my_account', {});
    this.navigationService.goTo(`profile`);
  }

  async checkIfUserIsEligibleForPromo() {
    const hasClaimedPromo = this.userInfo.claimedPromos ? this.userInfo.claimedPromos.includes(this.currentPromo.name) : false;
    const hasSmartlinkExpirationDays = this.currentPromo.smartlinkExpirationDays;

    if (hasClaimedPromo) { return false; }
    if (!hasSmartlinkExpirationDays) { return true; }

    const collection = this.firebaseService.getCollection('smartlinkPayments');
    const smartlinkRefs = await collection.where('userId', '==', this.userInfo.uid).get();
    if (!smartlinkRefs.size) { return false; }
    const smartlinks = smartlinkRefs.docs.map(doc => doc.data());
    const mostRecentSmartlink =
      smartlinks.sort((a, b) => moment(b.createdAt.toDate()).diff(moment(a.createdAt.toDate())))[0];

    const smartlinkDuration =
      moment(mostRecentSmartlink.expirationDate.toDate()).diff(moment(mostRecentSmartlink.createdAt.toDate()), 'days');
    if (smartlinkDuration < 30) { return false; }

    const isSmartlinkExpired = mostRecentSmartlink.expired;
    let expiredDays: number;
    if (isSmartlinkExpired) { expiredDays = Math.abs(Utils.calculateDaysRemaingSmartlink(mostRecentSmartlink)); }
    const daysRemaining = Utils.calculateDaysRemaingSmartlink(mostRecentSmartlink);
    const valid = isSmartlinkExpired
      ? expiredDays <= this.currentPromo.smartlinkMaxExpirationDays
      : daysRemaining <= this.currentPromo.smartlinkExpirationDays;
    return valid;
  }

  checkIfPromoIsActive() {
    if (this.currentPromo.validDate) {
      return moment().isBefore(moment(this.currentPromo.validDate.toDate()).endOf('day'));
    }
    return this.currentPromo.active;
  }
}
